import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currentOwnerId, useSession, useSessionExpired } from './auth.js';
import { AcknowledgeScreen } from './components/AcknowledgeScreen.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { LoginForm } from './components/LoginForm.js';
import { JournalScreen } from './components/journal/JournalScreen.js';
import type { JournalView } from './components/journal/JournalScreen.js';
import type { NoteEventPreset } from './components/journal/DayNotes.js';
import { QuickCheckIn } from './components/journal/QuickCheckIn.js';
import { JournalLockGate } from './components/journal/JournalLockGate.js';
import { watchBackground } from './journalLock.js';
import { SettingsScreen } from './components/SettingsScreen.js';
import { SetupScreen, SetupTile, useSetupHidden, useSetupStatus } from './components/Setup.js';
import type { SetupPage, SetupTarget } from './components/Setup.js';
import { writeSetupHidden } from './onboarding.js';
import { PrepTile } from './components/PrepTile.js';
import { TodayReminders } from './components/TodayReminders.js';
import { TodayTraining } from './components/TodayTraining.js';
import { PlanScreen } from './components/plan/PlanScreen.js';
import type { PlanView } from './components/plan/PlanScreen.js';
import { TodayTasks } from './components/TodayTasks.js';
import { WeekRings } from './components/WeekRings.js';
import { FamilyDisplay } from './components/family/FamilyDisplay.js';
import { SyncBadge, SyncProblem } from './components/SyncBadge.js';
import {
  IconCommunity,
  IconCosts,
  IconFamily,
  IconJournal,
  IconMembers,
  IconPlan,
  IconNutrition,
  IconSettings,
  IconShopping,
  IconToday,
  IconTraining,
} from './components/Icons.js';
import { RoomSheet, roomLabelKey } from './components/rooms/RoomSheet.js';
import { RoomIcon } from './components/rooms/RoomIcon.js';
import { HouseholdToday } from './components/rooms/HouseholdToday.js';
import { HouseholdMeals } from './components/meals/HouseholdMeals.js';
import { CommunityRoom, FamilyRoom } from './components/rooms/RoomPages.js';
import {
  barTabFor,
  defaultScope,
  readRoom,
  resolvePlace,
  roomTabs,
  roomsFor,
  writeRoom,
} from './rooms.js';
import type { Room, RoomTab } from './rooms.js';
import { useSwipeUp } from './gestures/SwipeProvider.js';
import { NutritionScreen } from './components/nutrition/NutritionScreen.js';
import { TodayEstimates } from './components/nutrition/TodayEstimates.js';
import type { NutritionView } from './components/nutrition/NutritionScreen.js';
import { ShoppingScreen } from './components/shopping/ShoppingScreen.js';
import type { ShoppingView } from './components/shopping/ShoppingScreen.js';
import { TrainingScreen } from './components/training/TrainingScreen.js';
import type { TrainingView } from './components/training/TrainingScreen.js';
import type {
  NotificationAction,
  NotificationTarget,
  NotificationView,
} from './push/handleNotificationAction.js';
import { usePush } from './push/usePush.js';
import { useNotificationTarget } from './push/useNotificationTarget.js';
import { estimateTargetDate } from './db/estimates.js';
import { useUpdateAvailable } from './pwa/updateState.js';
import { useSync } from './sync/useSync.js';
import { db } from './db/local.js';
import { today } from './db/nutrition.js';
import { readProfile } from './db/profile.js';
import { useIsChild, useModules } from './hooks/useModules.js';
import { ForMeTile } from './components/shopping/Children.js';
import { useTyping } from './hooks/useTyping.js';
import { SwipeProvider } from './gestures/SwipeProvider.js';
import { readSync } from './components/SyncBadge.js';

/**
 * Die App-Hülle.
 *
 * Ohne Sitzung gibt es das Anmeldeformular und **sonst nichts** — kein Gerüst,
 * keine Reiter, keine Anfragen an geschützte Endpunkte. Der Schutz sitzt zwar in
 * der API (dort wird er auch getestet), aber eine Oberfläche, die Daten
 * andeutet, die sie nicht laden darf, verwirrt nur.
 */
export function App(): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();

  if (session.isPending) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <p role="status" className="text-sm text-ink-muted">
          {t('session.loading')}
        </p>
      </main>
    );
  }

  if (!session.data) {
    return <LoginForm />;
  }

  return <Shell email={session.data.email} />;
}

type Tab = RoomTab;

function Shell({ email }: { email: string }): React.JSX.Element {
  const { t } = useTranslation();
  const sync = useSync(true);
  const push = usePush(true);

  // Sitzung abgelaufen: zur Anmeldung, aber **nichts verwerfen** — was noch in
  // der Outbox liegt, geht nach dem Anmelden raus.
  const sessionExpired = useSessionExpired();
  const unauthorized = sync.lastResult?.errorKind === 'unauthorized';
  useEffect(() => {
    if (unauthorized) sessionExpired();
  }, [unauthorized, sessionExpired]);

  // Tagebuch-Sperre: in den Hintergrund heißt wieder gesperrt.
  useEffect(() => watchBackground(), []);

  // Kam die App aus einer Benachrichtigung? Dann die gewählte Aktion ausführen
  // — und in jedem Fall die betroffene Zeile hervorheben. Auf iOS gibt es keine
  // Aktions-Knöpfe, dort ist das Antippen der einzige Weg; in einer Liste mit
  // sieben Einträgen muss man die richtige dann nicht suchen.
  const [highlightIntakeId, setHighlightIntakeId] = useState<string | null>(null);

  // Ohne gewählte Aktion (auf iOS der Normalfall) führt das Antippen auf eine
  // eigene Seite mit zwei großen Flächen — siehe AcknowledgeScreen.
  const [askIntakeId, setAskIntakeId] = useState<string | null>(null);

  // Eine Vorbereitungs-Erinnerung führt auf „Heute" zur Kachel „Für morgen".
  const [highlightPrep, setHighlightPrep] = useState(false);
  const [highlightTraining, setHighlightTraining] = useState(false);
  const [highlightTask, setHighlightTask] = useState<string | null>(null);
  const [planView, setPlanView] = useState<PlanView>('calendar');

  // Räume (27.10.2026): der gemerkte Raum je Gerät, ab Werk Ich. Ein Raum
  // beginnt auf seinem ersten Reiter.
  const [room, setRoom] = useState<Room>(readRoom);
  const [tab, setTab] = useState<Tab>(() => ROOM_START[readRoom()]);
  /** Ein Ort in der App — Raum und Reiter immer zusammen, nie nur der Reiter. */
  const go = useCallback((nextRoom: Room, nextTab: Tab) => {
    setRoom(nextRoom);
    setTab(nextTab);
  }, []);
  useEffect(() => writeRoom(room), [room]);
  // Familienanzeige (27.10.2026): Vollbild über allem, der Abgleich läuft weiter.
  const [familyDisplay, setFamilyDisplay] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  // Module (E15): Reiter und Kacheln nur für Eingeschaltetes. Wird der offene
  // Reiter abgeschaltet, geht es zurück auf „Heute“.
  const modules = useModules();
  // Kinder (E16): eigene Kachel „Für dich“, keine Einrichtung für Erwachsene.
  const isChild = useIsChild();
  const ownerId = currentOwnerId();
  const rooms = roomsFor(modules, isChild);
  const visibleTabs = roomTabs(room, modules, isChild);
  const on = (module: (typeof modules)[number]) => modules.includes(module);
  // Raum oder Reiter abgeschaltet (Modul, Kind)? Zurück auf Ich bzw. den
  // ersten Reiter des Raums.
  const place = resolvePlace({ room, tab }, modules, isChild);
  useEffect(() => {
    if (place.room !== room || place.tab !== tab) go(place.room, place.tab);
  }, [place.room, place.tab, room, tab, go]);
  // Einstellungen liegen über dem Reiter, nicht als sechster Reiter daneben:
  // Man braucht sie selten, und die Leiste ist für den Daumen voll.
  const [settings, setSettings] = useState(false);
  const [settingsFocus, setSettingsFocus] = useState<'calendar' | null>(null);
  const [journalDate, setJournalDate] = useState<string | null>(null);

  // Ersteinrichtung (UX-Welle 2): Kachel auf „Heute", Schritte darüber.
  const [setupPage, setSetupPage] = useState<SetupPage | null>(null);
  const setupStatus = useSetupStatus(push);
  const setupHidden = useSetupHidden();
  // Aus der Einrichtung direkt in eine Unteransicht (Supplements, Haushalt, Pläne).
  const [entryViews, setEntryViews] = useState<{
    training?: TrainingView;
    nutrition?: NutritionView;
    shopping?: ShoppingView;
  }>({});
  // Vom Einkaufstag auf „Heute“: die Liste dieses Ladens.
  const [shoppingShop, setShoppingShop] = useState<string | null>(null);
  const navigateFromSetup = (target: SetupTarget) => {
    setSetupPage(null);
    setSettings(false);
    if (target === 'calendar') {
      setSettingsFocus('calendar');
      setSettings(true);
      return;
    }
    if (target === 'supplements') {
      setEntryViews({ nutrition: 'supplements' });
      go('me', 'nutrition');
    } else if (target === 'household') {
      setEntryViews({ shopping: 'members' });
      go('household', 'members');
    } else {
      setEntryViews({ training: 'plans' });
      go('me', 'training');
    }
  };

  /** Ins Tagebuch — mit `date` direkt auf den Tag, sonst auf heute. */
  const openJournal = (date: string | null) => {
    setSettings(false);
    setSetupPage(null);
    go('me', 'journal');
    if (date) {
      setJournalDate(date);
      return;
    }
    void readProfile(db).then((profile) => setJournalDate(today(profile.timezone)));
  };

  const goToTarget = useCallback(
    (target: NotificationTarget, action: NotificationAction) => {
      // Jedes Ziel liegt im Raum Ich auf „Heute". Lief die App schon, stand sie
      // womöglich auf einem anderen Reiter oder in einem anderen Raum — dorthin
      // zurück wäre nach der Entscheidung falsch (Geräteabnahme 10.10.2026).
      setFamilyDisplay(false);
      setSettings(false);
      setSetupPage(null);
      if (target.kind === 'meal' || target.kind === 'water') {
        // Mahlzeit und Wasser: in den Tag der Ernährung, dort wird eingetragen.
        setEntryViews({ nutrition: 'day' });
        go('me', 'nutrition');
        return;
      }
      go('me', 'today');
      if (target.kind === 'prep') {
        setHighlightPrep(true);
        return;
      }
      if (target.kind === 'training') {
        setHighlightTraining(true);
        return;
      }
      if (target.kind === 'task') {
        setHighlightTask(target.id);
        return;
      }
      if (action.action) setHighlightIntakeId(target.id);
      else setAskIntakeId(target.id);
    },
    [go],
  );

  // Der Wochenrückblick am Sonntag führt ins Tagebuch, Ansicht „Woche".
  const [journalView, setJournalView] = useState<JournalView | null>(null);
  // Standort (27.10.2026): Haushalt → Mitglieder, neu aufgebaut, damit die
  // Ansicht auch dann wechselt, wenn der Reiter schon offen war.
  const [locationVisit, setLocationVisit] = useState(0);
  // Foto-Schätzung (28.10.2026): in den Tag mit dem Platzhalter — neu
  // aufgebaut, damit der Tag auch dann wechselt, wenn der Reiter offen war.
  const [nutritionVisit, setNutritionVisit] = useState<{ date: string | null; visit: number }>({
    date: null,
    visit: 0,
  });
  const { syncNow } = sync;
  const goToView = useCallback(
    (view: NotificationView) => {
      setFamilyDisplay(false);
      setSettings(false);
      setSetupPage(null);
      if (view === 'estimate') {
        // Erst abholen (das Ergebnis liegt beim Öffnen meist noch nicht lokal),
        // dann der Tag des ältesten Platzhalters — sonst heute.
        syncNow();
        void estimateTargetDate(db).then((date) => {
          setEntryViews({ nutrition: 'day' });
          setNutritionVisit((current) => ({ date, visit: current.visit + 1 }));
          go('me', 'nutrition');
        });
        return;
      }
      if (view === 'location') {
        setEntryViews({ shopping: 'members' });
        setLocationVisit((visit) => visit + 1);
        go('household', 'members');
        return;
      }
      setJournalDate(null);
      go('me', 'journal');
      if (view === 'week') setJournalView('week');
    },
    [go, syncNow],
  );
  const viewShown = useCallback(() => setJournalView(null), []);
  // Aus dem Kalender: „Eintrag dazu schreiben" (E14).
  const [notePreset, setNotePreset] = useState<NoteEventPreset | null>(null);
  const notePresetUsed = useCallback(() => setNotePreset(null), []);

  useNotificationTarget(goToTarget, sync.syncNow, goToView);
  // Zurück dorthin, wo sie geöffnet wurde (Haushalt → Heute oder Mitglieder):
  // Raum und Reiter stehen unverändert.
  const exitFamilyDisplay = useCallback(() => setFamilyDisplay(false), []);

  /** Zur Einkaufsliste (vom Einkaufstag: gleich die Liste dieses Ladens). */
  const openShoppingList = (shopId: string | null) => {
    setShoppingShop(shopId);
    setEntryViews({ shopping: 'list' });
    go('household', 'shopping');
  };

  if (askIntakeId) {
    return (
      <AcknowledgeScreen
        intakeId={askIntakeId}
        onDone={() => {
          setHighlightIntakeId(askIntakeId);
          setAskIntakeId(null);
        }}
      />
    );
  }

  if (familyDisplay && !isChild && on('household')) {
    return (
      <ErrorBoundary scope="screen">
        <FamilyDisplay planning={on('planning')} onExit={exitFamilyDisplay} />
      </ErrorBoundary>
    );
  }

  const changeTab = (next: Tab) => {
    setSettings(false);
    setSetupPage(null);
    setEntryViews({});
    if (next === 'journal') setJournalDate(null);
    setTab(next);
  };

  return (
    <SwipeProvider
      contentRef={mainRef}
      onRefresh={async () => {
        const outcome = await sync.refresh();
        return readSync(
          {
            isOnline: outcome.online,
            failed: outcome.failed,
            held: outcome.held,
            lastResult: outcome.result,
            isSyncing: false,
            pending: outcome.pending,
          },
          t,
        ).label;
      }}
    >
      <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col">
        {/* Kopfzeile als Glas, bleibt beim Scrollen oben — der Inhalt läuft
          weichgezeichnet darunter durch. */}
        <header className="kw-glass sticky top-[calc(env(safe-area-inset-top)+0.5rem)] z-10 mx-3 mt-3 mb-3 flex items-center justify-between gap-3 rounded-[1.5rem] px-3 py-2">
          <h1 className="shrink-0 font-display text-2xl font-bold tracking-wide uppercase">
            {t('app.name')}
          </h1>
          <div className="flex min-w-0 items-center gap-2">
            <SyncBadge state={sync} />
            <button
              type="button"
              aria-label={t('settings.heading')}
              aria-pressed={settings}
              onClick={() => {
                setSetupPage(null);
                setSettingsFocus(null);
                setSettings((value) => !value);
              }}
              className={`flex size-11 shrink-0 items-center justify-center rounded-full border ${
                settings ? 'border-accent text-accent' : 'border-line text-ink-muted'
              }`}
            >
              <IconSettings />
            </button>
          </div>
        </header>

        {/* pb-28 hält den Inhalt über der schwebenden Leiste — sonst liegt die
          letzte Zeile jedes Bildschirms darunter und ist nicht erreichbar. */}
        <main ref={mainRef} className="flex flex-1 flex-col gap-3 px-3 pb-28">
          <UpdateBanner />
          <SyncProblem state={sync} />

          <ErrorBoundary
            key={setupPage ? 'setup' : settings ? 'settings' : `${room}-${tab}`}
            scope="screen"
          >
            {setupPage && setupStatus ? (
              <SetupScreen
                page={setupPage}
                status={setupStatus}
                push={push}
                onPage={setSetupPage}
                onClose={() => setSetupPage(null)}
                onNavigate={navigateFromSetup}
              />
            ) : null}

            {!setupPage && settings ? (
              <SettingsScreen
                email={email}
                push={push}
                focus={settingsFocus}
                onClose={() => setSettings(false)}
                onSync={sync.refresh}
                onOpenSetup={() => {
                  void writeSetupHidden(false, db);
                  setSettings(false);
                  go('me', 'today');
                  setSetupPage(setupStatus?.next ?? 'install');
                }}
              />
            ) : null}

            {/* „Heute" zeigt nur, was jetzt dran ist. Alles Einmalige liegt hinter
            dem Zahnrad, alles Ausführliche im Tagebuch. */}
            {!setupPage && !settings && tab === 'today' ? (
              <>
                {isChild && ownerId ? <ForMeTile me={ownerId} /> : null}

                {!isChild && setupStatus && setupHidden === false && !setupStatus.complete ? (
                  <SetupTile status={setupStatus} onOpen={setSetupPage} />
                ) : null}

                {/* Befinden zuerst: Es ist morgens die erste Handlung und
                  verschwindet, sobald es ausgefüllt ist. */}
                {on('journal') ? <QuickCheckIn onOpenJournal={() => openJournal(null)} /> : null}

                {on('nutrition') ? <TodayReminders highlightIntakeId={highlightIntakeId} /> : null}

                {on('nutrition') ? <TodayEstimates onOpen={() => goToView('estimate')} /> : null}

                {on('planning') ? (
                  <TodayTasks
                    highlightId={highlightTask}
                    onOpenTasks={() => {
                      setPlanView('tasks');
                      go('me', 'plan');
                    }}
                  />
                ) : null}

                {on('training') ? (
                  <TodayTraining
                    highlight={highlightTraining}
                    onOpenTraining={() => go('me', 'training')}
                  />
                ) : null}

                {on('nutrition') ? <PrepTile highlight={highlightPrep} /> : null}

                <WeekRings
                  onSetup={(ring) =>
                    ring === 'supplements'
                      ? navigateFromSetup('supplements')
                      : setSetupPage(ring === 'protein' ? 'body' : 'training')
                  }
                />
              </>
            ) : null}

            {!setupPage && !settings && tab === 'training' ? (
              <TrainingScreen initialView={entryViews.training} />
            ) : null}

            {!setupPage && !settings && tab === 'nutrition' ? (
              <NutritionScreen
                key={`nutrition-${nutritionVisit.visit}`}
                initialView={entryViews.nutrition}
                initialDate={nutritionVisit.date}
                onOpenSettings={isChild ? undefined : () => setSetupPage('body')}
              />
            ) : null}

            {!setupPage && !settings && tab === 'plan' ? (
              <PlanScreen
                key={`${room}-${planView}`}
                initialView={planView}
                initialScope={defaultScope(room)}
                focusTaskId={planView === 'tasks' ? highlightTask : null}
                onOpenTraining={() => go('me', 'training')}
                onRefreshed={sync.syncNow}
                onConnectCalendar={() => navigateFromSetup('calendar')}
                onWriteNote={(preset) => {
                  setNotePreset(preset);
                  setJournalDate(preset.date);
                  go('me', 'journal');
                }}
              />
            ) : null}

            {/* Raum Haushalt (27.10.2026): Heute im Haushalt. */}
            {!setupPage && !settings && tab === 'home' ? (
              <HouseholdToday
                planning={on('planning')}
                child={isChild}
                onOpenList={openShoppingList}
                onOpenPlan={() => {
                  setPlanView('tasks');
                  go('household', 'plan');
                }}
                onOpenFamilyDisplay={isChild ? undefined : () => setFamilyDisplay(true)}
                onOpenMembers={() => go('household', 'members')}
              />
            ) : null}

            {/* Haushalts-Essensplan (27.10.2026). */}
            {!setupPage && !settings && tab === 'meals' ? (
              <HouseholdMeals onOpenList={() => openShoppingList(null)} />
            ) : null}

            {!setupPage && !settings && tab === 'family' ? (
              <FamilyRoom
                onOpenHouseholdPlan={
                  rooms.includes('household') && on('planning')
                    ? () => {
                        setPlanView('calendar');
                        go('household', 'plan');
                      }
                    : undefined
                }
              />
            ) : null}

            {!setupPage && !settings && tab === 'community' ? <CommunityRoom /> : null}

            {/* Einkauf, Kosten bzw. Taschengeld und Mitglieder sind im Raum
              Haushalt eigene Reiter — derselbe Bildschirm, je ein Ausschnitt. */}
            {!setupPage &&
            !settings &&
            (tab === 'shopping' || tab === 'costs' || tab === 'pocket' || tab === 'members') ? (
              <ShoppingScreen
                key={`${tab}-${locationVisit}`}
                views={HOUSEHOLD_VIEWS[tab]}
                focusLocation={locationVisit > 0}
                initialView={entryViews.shopping}
                initialShopId={shoppingShop}
                nutrition={on('nutrition')}
                household={on('household')}
                costs={!isChild}
                pocket={isChild}
                onMembershipChanged={sync.syncNow}
                onOpenFamilyDisplay={isChild ? undefined : () => setFamilyDisplay(true)}
              />
            ) : null}

            {!setupPage && !settings && tab === 'journal' ? (
              <JournalLockGate>
                <JournalScreen
                  openDate={journalDate}
                  onDateChange={setJournalDate}
                  requestedView={journalView}
                  onViewShown={viewShown}
                  notePreset={notePreset}
                  onNotePresetUsed={notePresetUsed}
                />
              </JournalLockGate>
            ) : null}
          </ErrorBoundary>
        </main>

        <TabBar
          room={room}
          rooms={rooms}
          tab={barTabFor(room, tab)}
          visible={visibleTabs}
          onChange={changeTab}
          onRoom={(next) => {
            changeTab(roomTabs(next, modules, isChild)[0]!);
            setRoom(next);
          }}
        />
      </div>
    </SwipeProvider>
  );
}

/** Wo ein Raum beginnt — bevor die Module geladen sind (`resolvePlace` korrigiert). */
const ROOM_START = {
  me: 'today',
  household: 'home',
  family: 'family',
  community: 'community',
} as const satisfies Record<Room, Tab>;

/** Welche Ausschnitte des Haushalt-Bildschirms ein Reiter im Raum Haushalt zeigt. */
const HOUSEHOLD_VIEWS = {
  shopping: ['list', 'pantry'],
  costs: ['costs'],
  pocket: ['pocket'],
  members: ['members'],
} as const satisfies Record<string, readonly ShoppingView[]>;

/**
 * Die schwebende Reiterleiste.
 *
 * Sechs gleich breite Plätze, Name immer sichtbar: links der **Raum-Knopf**
 * (27.10.2026), daneben bis zu fünf Reiter des Raums. Der Raum-Knopf zeigt,
 * wo man ist; Antippen öffnet das Raum-Blatt, **Hochwischen auf dem Knopf**
 * ist die Abkürzung dafür. Die Leiste selbst wird nie gewischt
 * (`data-no-swipe`, Timo 24.10.2026) — und vom unteren Bildschirmrand schon
 * gar nicht, dort liegt die Home-Geste von iOS (`swipeUpOpens`).
 *
 * Gibt es nur einen Raum (ein Kind ohne Haushalt), entfällt der Knopf.
 */
function TabBar({
  room,
  rooms,
  tab,
  visible,
  onChange,
  onRoom,
}: {
  room: Room;
  /** Räume nach Modulen und Rolle — `roomsFor`. */
  rooms: readonly Room[];
  tab: Tab;
  /** Reiter des Raums (E15) — `roomTabs`. */
  visible: readonly Tab[];
  onChange: (tab: Tab) => void;
  onRoom: (room: Room) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [sheet, setSheet] = useState(false);
  const roomButton = useSwipeUp<HTMLButtonElement>(() => setSheet(true));
  const closeSheet = useCallback(() => {
    setSheet(false);
    roomButton.current?.focus();
  }, [roomButton]);

  const all: Record<Tab, { label: string; icon: React.JSX.Element }> = {
    today: { label: t('nav.today'), icon: <IconToday /> },
    home: { label: t('nav.today'), icon: <IconToday /> },
    training: { label: t('nav.training'), icon: <IconTraining /> },
    nutrition: { label: t('nav.nutrition'), icon: <IconNutrition /> },
    journal: { label: t('nav.journal'), icon: <IconJournal /> },
    plan: { label: t('nav.plan'), icon: <IconPlan /> },
    shopping: { label: t('nav.shopping'), icon: <IconShopping /> },
    costs: { label: t('nav.costs'), icon: <IconCosts /> },
    pocket: { label: t('nav.pocket'), icon: <IconCosts /> },
    members: { label: t('nav.members'), icon: <IconMembers /> },
    meals: { label: t('nav.meals'), icon: <IconNutrition /> },
    family: { label: t('nav.family'), icon: <IconFamily /> },
    community: { label: t('nav.community'), icon: <IconCommunity /> },
  };
  const typing = useTyping();
  const roomName = t(roomLabelKey[room]);

  return (
    <>
      <nav
        aria-label={t('app.name')}
        // Beim Tippen ausgeblendet (`useTyping`) — iOS ließe sie sonst mitscrollen.
        hidden={typing}
        data-no-swipe=""
        className="fixed inset-x-0 bottom-0 z-10 px-2 pb-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}
      >
        {/* Jeder Platz höchstens ein Sechstel: Ein Raum mit einem Reiter
          (Familie) bekommt keine halbe Leiste breite Knöpfe. */}
        <ul className="kw-glass mx-auto flex max-w-2xl justify-center gap-0.5 rounded-[1.75rem] p-1">
          {rooms.length > 1 ? (
            <li className="max-w-[16.667%] min-w-0 flex-1">
              <button
                ref={roomButton}
                type="button"
                aria-label={t('rooms.switch', { room: roomName })}
                aria-haspopup="dialog"
                aria-expanded={sheet}
                onClick={() => setSheet(true)}
                // Kein Bernstein: Der Knopf zeigt den Ort, er ist nicht die nächste
                // Handlung. Die Linie trennt ihn von den Reitern.
                className="flex min-h-14 w-full touch-none flex-col items-center justify-center gap-0.5 rounded-2xl border border-line px-0.5 text-ink"
              >
                <RoomIcon room={room} />
                <span className="w-full truncate text-center text-[11px] leading-tight font-semibold tracking-tight">
                  {roomName}
                </span>
              </button>
            </li>
          ) : null}
          {visible.map((key) => {
            const entry = all[key];
            const active = tab === key;
            // Gleich breite Reiter, Name immer sichtbar: Eine wachsende Pille
            // für den aktiven Namen passte bei 375 px nicht, und neue Nutzer
            // kennen die Symbole noch nicht.
            return (
              <li key={key} className="max-w-[16.667%] min-w-0 flex-1">
                <button
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => onChange(key)}
                  className={`flex min-h-14 w-full flex-col items-center justify-center gap-0.5 rounded-2xl px-0.5 ${
                    active ? 'bg-accent text-ground' : 'text-ink-muted'
                  }`}
                >
                  {entry.icon}
                  <span className="w-full truncate text-center text-[11px] leading-tight tracking-tight">
                    {entry.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      {sheet ? (
        <RoomSheet
          rooms={rooms}
          current={room}
          onClose={closeSheet}
          onPick={(next) => {
            if (next !== room) onRoom(next);
            closeSheet();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Neue Version bereit. Von selbst lädt sie nur, wenn es ruhig ist
 * (`pwa/update.ts`) — mitten in der Satzpause ginge sonst der Timer verloren.
 * Verlangt der Server sie, sagt die Zeile, dass bis dahin nichts abgleicht.
 */
function UpdateBanner(): React.JSX.Element | null {
  const { t } = useTranslation();
  const update = useUpdateAvailable();
  if (!update.available) return null;

  return (
    <div
      role="status"
      className="kw-tile flex flex-wrap items-center justify-between gap-2 border-accent"
    >
      <p className="text-sm">{t(update.required ? 'update.required' : 'update.available')}</p>
      <button type="button" onClick={update.apply} className="kw-btn kw-btn-small px-3">
        {t('update.apply')}
      </button>
    </div>
  );
}
